export interface Amount {
  value: number
  currency: string
}

export interface AmountWithType extends Amount {
  type: 'constant' | 'coefficient'
}

export type DiscountItemId = string & { readonly CustomerId: unique symbol }
export type ProductId = string & { readonly DiscountItemId: unique symbol }

export type Discounts = {
  modifier: string
  type: string
  value: number
  adornment: string
  expirationDate: Date | undefined
}

export interface DiscountItemPrice {
  type: string
  amount: Amount
}

export interface DiscountItemObject {
  id: DiscountItemId
  name: string
  price: DiscountItemPrice | undefined
  discount: Discounts
}

export interface Product {
  id: ProductId
  name: string
  disabled: boolean
}

export interface CustomerDiscount {
  billableId: string
  name: string
  unit: string
  quantity: RangeQuantity | ExactQuantity
  price?: LinearBillablePrice
  discount: Discounts
}

interface RangeQuantity {
  type: 'range'
  min: number
  max: number
}

interface ExactQuantity {
  type: 'exact'
  value: number
}

export type BillablePrice = LinearBillablePrice

export interface LinearBillablePrice {
  billableId: string
  type: 'linear'
  coefficients: Amount[]
  constants: Amount[]
}

export interface Billable {
  billableId: string
  name: string
  unitName: string
}

export interface Template {
  id: string
  name: string
  externalName: string
}

export interface TemplateRelation {
  id: string
  name: string
  relatives: TemplateRelationRelative[]
}

interface TemplateRelationRelative {
  id: string
  name: string
  externalName: string
}

export enum DiscountType {
  FlatDiscount = 'FlatDiscount',
  PerUnitDiscount = 'PerUnitDiscount',
  PerUnitAndFlatDiscount = 'PerUnitAndFlatDiscount',
  FirstNFreeDiscount = 'FirstNFreeDiscount',
}

export enum DiscountModifier {
  PERCENTAGE = 'percentage',
  ABSOLUTE = 'absolute',
}

interface FlatDiscountBase {
  type: DiscountType.FlatDiscount
  modifier: DiscountModifier
}

interface FlatDiscountPercentage extends FlatDiscountBase {
  modifier: DiscountModifier.PERCENTAGE
  percentageValue: number
}

interface FlatDiscountAbsolute extends FlatDiscountBase {
  modifier: DiscountModifier.ABSOLUTE
  absoluteAmount: Amount
}

export type FlatDiscount = FlatDiscountPercentage | FlatDiscountAbsolute

interface PerUnitDiscountBase {
  type: DiscountType.PerUnitDiscount
  modifier: DiscountModifier
}

interface PerUnitDiscountPercentage extends PerUnitDiscountBase {
  modifier: DiscountModifier.PERCENTAGE
  percentageValue: number
}

interface PerUnitDiscountAbsolute extends PerUnitDiscountBase {
  modifier: DiscountModifier.ABSOLUTE
  absoluteAmount: Amount
}

export type PerUnitDiscount = PerUnitDiscountPercentage | PerUnitDiscountAbsolute
interface PerUnitAndFlatDiscountBase {
  type: DiscountType.PerUnitAndFlatDiscount
  modifier: DiscountModifier
}

interface PerUnitAndFlatDiscountPercentage extends PerUnitAndFlatDiscountBase {
  modifier: DiscountModifier.PERCENTAGE
  percentageValue: number
}

interface PerUnitAndFlatDiscountAbsolute extends PerUnitAndFlatDiscountBase {
  modifier: DiscountModifier.ABSOLUTE
  absoluteAmount: Amount
}

export interface FirstNFreeDiscount {
  type: DiscountType.FirstNFreeDiscount
  numberOfFreeItems: number
}

export type PerUnitAndFlatDiscount = PerUnitAndFlatDiscountPercentage | PerUnitAndFlatDiscountAbsolute

export type AllDiscounts = FlatDiscount | PerUnitDiscount | PerUnitAndFlatDiscount | FirstNFreeDiscount

export interface CreateDiscount<T = AllDiscounts> {
  billableId: string
  discountType: T
  expirationDate?: string
}
